package com.evoapps.evoshift.domain.schedule

import com.evoapps.evoshift.domain.model.Absence
import com.evoapps.evoshift.domain.model.AbsenceStatus
import com.evoapps.evoshift.domain.model.AvailabilityStatus
import com.evoapps.evoshift.domain.model.BusyElsewhere
import com.evoapps.evoshift.domain.model.DayPreference
import com.evoapps.evoshift.domain.model.Shift
import com.evoapps.evoshift.domain.model.User
import com.evoapps.evoshift.domain.util.calculateDurationHours

/** SH-167: what a person said about the shift's day, as the day sheet's picker groups people. */
enum class DayAnswer {
    /** Said they are available that day (and are not absent). */
    AVAILABLE,
    /** Has not answered for that day (or the month was not asked). */
    NO_ANSWER,
    /** Said they are not available, or is absent (approved leave or sick leave). */
    UNAVAILABLE
}

/**
 * SH-167: one person the day sheet offers for an unassigned shift, with what the manager needs to
 * choose: their answer for the day, their planned hours this month (and the contracted hours,
 * where readable), and what stands in the way (an absence, work elsewhere, another shift here).
 */
data class AssignCandidate(
    val user: User,
    val answer: DayAnswer,
    /** Their answer for the day as given (slot, comment); null: none. */
    val preference: DayPreference?,
    /** Planned hours this month before this shift. */
    val monthHours: Double,
    /** Contracted hours this month; null when not known (or not the reader's to see). */
    val targetHours: Double?,
    /** This shift would take them over [targetHours]. */
    val overTarget: Boolean,
    /** An approved absence covering the day. */
    val absence: Absence?,
    /** Their work at other workplaces that day. */
    val busyElsewhere: List<BusyElsewhere>,
    /** Their other shifts here that day. */
    val sameDayShifts: List<Shift>,
    /** One of [sameDayShifts] or [busyElsewhere] overlaps the shift. */
    val overlaps: Boolean,
    /**
     * SH-267: someone borrowed from another workplace of the group: its id and name (null: one of
     * the workplace's own people).
     */
    val guestFrom: GuestSource? = null,
    /** SH-267: a borrowed person is away that day (an approved absence at home; the reason is not ours to know). */
    val awayAtHome: Boolean = false
) {
    /** Something stands in the way of this person working the shift. */
    val hasConflict: Boolean get() = absence != null || awayAtHome || overlaps
}

/** SH-267: where a borrowed person comes from. */
data class GuestSource(val workplaceId: String, val workplaceName: String)

/**
 * SH-167: the people to offer for an unassigned shift in the day sheet, best first: those
 * available that day, then those who have not answered, then the unavailable; people who cannot
 * take it anyway (absent, or working at that time here or elsewhere) come after all of them; by
 * name within each. Only people with the shift's position (any, when it needs none) and all its
 * skills.
 */
object AssignCandidates {

    fun rank(
        shift: Shift,
        team: List<User>,
        workplaceId: String,
        /** The day's shifts here (the shift's own included or not). */
        dayShifts: List<Shift>,
        /** Each person's answer for the shift's day. */
        preferences: Map<String, DayPreference>,
        plannedHours: Map<String, Double>,
        targetHours: Map<String, Double>,
        absences: List<Absence>,
        /** Each person's work elsewhere on the shift's day. */
        busyElsewhere: Map<String, List<BusyElsewhere>>
    ): List<AssignCandidate> {
        val hours = calculateDurationHours(shift.plannedTime.start, shift.plannedTime.end)
        return team
            .filter { user ->
                val engagement = user.engagements[workplaceId] ?: return@filter false
                engagement.isActive &&
                    (shift.requiredRole.isNullOrBlank() || engagement.role == shift.requiredRole) &&
                    shift.requiredSkills.all { it in engagement.skills }
            }
            .map { user ->
                val preference = preferences[user.uid]
                val absence = absences.firstOrNull {
                    it.userId == user.uid && it.status == AbsenceStatus.APPROVED &&
                        (it.workplaceId == null || it.workplaceId == workplaceId) &&
                        it.startDate <= shift.date && shift.date <= it.endDate
                }
                val sameDay = dayShifts.filter { it.id != shift.id && it.userId == user.uid }
                val elsewhere = busyElsewhere[user.uid].orEmpty()
                val overlaps = sameDay.any { overlap(it.plannedTime.start, it.plannedTime.end, shift) } ||
                    elsewhere.any { overlap(it.plannedTime.start, it.plannedTime.end, shift) }
                val answer = when {
                    absence != null -> DayAnswer.UNAVAILABLE
                    preference?.status == AvailabilityStatus.AVAILABLE -> DayAnswer.AVAILABLE
                    preference?.status == AvailabilityStatus.UNAVAILABLE -> DayAnswer.UNAVAILABLE
                    else -> DayAnswer.NO_ANSWER
                }
                val month = plannedHours[user.uid] ?: 0.0
                val target = targetHours[user.uid]
                AssignCandidate(
                    user = user,
                    answer = answer,
                    preference = preference,
                    monthHours = month,
                    targetHours = target,
                    overTarget = target != null && month + hours > target + EPSILON,
                    absence = absence,
                    busyElsewhere = elsewhere,
                    sameDayShifts = sameDay,
                    overlaps = overlaps
                )
            }
            .sortedWith(
                compareBy<AssignCandidate> { it.hasConflict }
                    .thenBy { it.answer.ordinal }
                    .thenBy { it.user.displayName.lowercase() }
            )
    }

    /**
     * SH-267: the people of the other workplaces of the group to offer for [shift] ("From your
     * other workplaces"), ranked as the team is: their home position and skills, their answer for
     * the day as given at home, their month's hours with this owner, their work elsewhere and here
     * that day; a day they are away at home counts like an absence (without its kind).
     */
    fun rankGuests(
        shift: Shift,
        guests: List<com.evoapps.evoshift.domain.model.BorrowCandidate>,
        workplaceId: String,
        dayShifts: List<Shift>,
        busyElsewhere: Map<String, List<BusyElsewhere>>
    ): List<AssignCandidate> {
        val day = shift.date.substringAfterLast('-').toIntOrNull() ?: return emptyList()
        val byId = guests.associateBy { it.userId }
        return rank(
            shift = shift,
            team = guests.map { it.asTeamMember(workplaceId) },
            workplaceId = workplaceId,
            dayShifts = dayShifts,
            preferences = guests.mapNotNull { g -> g.days[day]?.let { g.userId to it } }.toMap(),
            plannedHours = guests.associate { it.userId to it.monthHours },
            targetHours = emptyMap(),
            absences = emptyList(),
            busyElsewhere = busyElsewhere
        ).map { candidate ->
            val guest = byId.getValue(candidate.user.uid)
            val away = day in guest.awayDays
            candidate.copy(
                guestFrom = GuestSource(guest.homeWorkplaceId, guest.homeWorkplaceName),
                awayAtHome = away,
                answer = if (away) DayAnswer.UNAVAILABLE else candidate.answer
            )
        }.sortedWith(
            compareBy<AssignCandidate> { it.hasConflict }
                .thenBy { it.answer.ordinal }
                .thenBy { it.user.displayName.lowercase() }
        )
    }

    /** Local ISO date-times ("2026-02-01T08:00:00") compare as text. */
    private fun overlap(start: String, end: String, shift: Shift): Boolean =
        start.isNotEmpty() && end.isNotEmpty() && start < shift.plannedTime.end && shift.plannedTime.start < end

    private const val EPSILON = 0.001
}
